import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type EmployeeOverview, employeesOverviewQueryOptions } from '@/entities/employee'
import { PersonalBoardLink } from '@/features/open-personal-board'
import { formatDuration } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { RunningTimer } from '@/shared/ui/running-timer'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/employees')({
  component: EmployeesPage,
  head: () => ({ meta: [{ title: 'Сотрудники · STIT Task' }] }),
})

// Группировка по первому отделу (имя, алфавит; без отделов — в конец), внутри — по
// имени. Сотрудник в нескольких отделах группируется по первому (departments[0]).
function byDepartmentThenName(a: EmployeeOverview, b: EmployeeOverview): number {
  const da = a.departments[0] ?? '￿'
  const db = b.departments[0] ?? '￿'
  return da !== db ? da.localeCompare(db) : a.name.localeCompare(b.name)
}

// Контекст задачи: «Проект · Доска» (board_name = имя доски).
function TaskContext({ boardName, projectName }: { boardName: string; projectName: string }) {
  const parts = [projectName, boardName].filter(Boolean)
  if (parts.length === 0) return null
  return <span className="truncate text-xs text-text-tertiary">{parts.join(' · ')}</span>
}

function EmployeeCard({ e }: { e: EmployeeOverview }) {
  return (
    <div className="flex flex-col gap-4 rounded-card bg-bg-surface p-5 ring-1 ring-border-subtle ring-inset">
      <div className="flex items-start gap-3">
        <Link
          to="/employees/$employeeId"
          params={{ employeeId: e.employeeId }}
          className="group flex min-w-0 flex-1 items-start gap-3"
        >
          <Avatar name={e.name} size="md" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate font-medium text-text-primary transition-colors group-hover:text-accent">
              {e.name}
            </span>
            {e.departments.length > 0 ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <DepartmentBadges names={e.departments} />
              </span>
            ) : (
              <span className="text-xs text-text-tertiary">Без отдела</span>
            )}
          </div>
        </Link>
        {e.focus && (
          <span className="flex shrink-0 items-center gap-1.5 text-xs text-text-secondary">
            <span className="dot-pulse size-2 rounded-pill bg-accent" aria-hidden />В фокусе
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-sm text-text-secondary">
          Всего{' '}
          <span data-numeric className="mono-num text-text-primary">
            {formatDuration(e.totalSeconds)}
          </span>
        </span>
        <span className="text-sm text-text-tertiary">
          сегодня{' '}
          <span data-numeric className="mono-num text-text-secondary">
            {formatDuration(e.todaySeconds)}
          </span>
        </span>
      </div>

      {e.timer && (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="label">Таймер</span>
          <div className="flex min-w-0 items-center gap-2">
            <RunningTimer startedAt={e.timer.startedAt} size="sm" />
            <Link
              to="/tasks/$taskId"
              params={{ taskId: e.timer.taskId }}
              className="min-w-0 flex-1 truncate text-sm text-text-secondary transition-colors hover:text-text-primary"
            >
              {e.timer.taskTitle}
            </Link>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {/* Незакрытые задачи сотрудника, а не идущий таймер: тот — свой блок выше. */}
        <span className="label">
          Назначено{' '}
          <span data-numeric className="mono-num text-text-secondary">
            {e.activeCount}
          </span>
        </span>
        {e.activeTasks.length === 0 ? (
          <span className="text-sm text-text-tertiary">Нет назначенных задач</span>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {e.activeTasks.map((t) => (
              <li key={t.taskId} className="flex min-w-0 flex-col">
                <Link
                  to="/tasks/$taskId"
                  params={{ taskId: t.taskId }}
                  className="truncate text-sm text-text-secondary transition-colors hover:text-text-primary"
                >
                  {t.title}
                </Link>
                <TaskContext boardName={t.boardName} projectName={t.projectName} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border-subtle pt-3 text-sm text-text-tertiary">
        <span>
          Выполнено{' '}
          <span data-numeric className="mono-num text-text-secondary">
            {e.completedCount}
          </span>
        </span>
        {/* Личная доска коллеги: и посмотреть, чем он занят помимо проектов, и
            закинуть ему задачу, у которой ещё нет проекта. */}
        <PersonalBoardLink
          employeeId={e.employeeId}
          ariaLabel={`Личная доска: ${e.name}`}
          className="inline-flex items-center gap-1.5 text-text-secondary transition-colors hover:text-text-primary disabled:opacity-60"
        >
          <Icon name="kanban" size={14} />
          Личная доска
        </PersonalBoardLink>
      </div>
    </div>
  )
}

function EmployeesPage() {
  const overview = useQuery(employeesOverviewQueryOptions)

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader title="Сотрудники" eyebrow="Команда" />
      <div className="mt-8">
        {overview.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner label="Загрузка сотрудников" />
          </div>
        ) : overview.isError ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-sm text-text-secondary">Не удалось загрузить сводку.</p>
            <Button variant="secondary" onClick={() => void overview.refetch()}>
              Повторить
            </Button>
          </div>
        ) : (overview.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={36} />}
            title="Пока нет сотрудников"
            description="Здесь появится команда с активными задачами и временем."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[...(overview.data ?? [])].sort(byDepartmentThenName).map((e) => (
              <EmployeeCard key={e.employeeId} e={e} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
